import { createMemo } from 'solid-js';
import { allocationsOf, holdingsOf } from '~data';
import { resolved } from '~/stores/portfolio-store';
import { positionAt } from '~/stores/positions';
import { classifiedSector } from '~/data/sectors';
import { classifiedCountry } from '~/data/countries';

// Look-through. Two covered-call ETFs on Canadian banks are not two positions —
// they are mostly the same five banks bought over and over, and nothing in the account
// view can show you that. This resolves every fund you hold down to what is
// actually inside it, along three axes: company, sector and geography.
//
// Weights are published as percentages (RY is `weight: 25` in HMAX, meaning
// 25%), so a slice is value × weight ÷ 100 on every axis.

/** One fund's contribution to something you are exposed to. */
export interface FundSlice {
    fund: string;
    /** The bucket's weight *within that fund*, as published. */
    weight: number;
    /** Dollars of your position in that fund attributable to this bucket. */
    value: number;
}

export interface Exposure {
    /** Ticker where one is published, otherwise a normalised name. */
    key: string;
    name: string;
    ticker: string | null;
    /** Every fund of yours reaching it, biggest slice first. */
    slices: FundSlice[];
    value: number;
    /** Percentage of the fund money that could be resolved *on this axis* — not
        of the whole book, which includes cash and any fund that publishes
        nothing. Those would dilute every row against a denominator none of it
        came from, and each axis resolves a different set of funds. */
    share: number | null;
}

export type Axis = 'company' | 'sector' | 'geography';

const SECTOR_KIND = 'Sector Allocation';
const GEOGRAPHIC_KIND = 'Geographic Allocation';

// Issuers write the same company differently — "Toronto-Dominion Bank/The" from
// one, "TORONTO-DOMINION BANK" from another — so names are matched only after
// being flattened to letters and digits. A ticker is preferred wherever it
// exists: it is the one identifier that actually means the same thing on both
// sides, and matching on it is why RY in a Hamilton fund lines up with RY in a
// Harvest one.
const normalize = (name: string): string => name.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim();

/** Market value per fund across every account: the same fund held in a TFSA and
    in a margin account is one exposure, not two. Unpriced positions are skipped
    — there is no honest dollar figure to attribute. */
const valueByFund = createMemo(() => {
    const byFund = new Map<string, number>();
    for (const r of resolved()) {
        if (r.value == null) continue;
        byFund.set(r.symbol, (byFund.get(r.symbol) ?? 0) + r.value);
    }
    return byFund;
});

export const heldFunds = createMemo(() => [...valueByFund().keys()].sort());

export interface Bucket {
    key: string;
    name: string;
    ticker: string | null;
    weight: number;
}

/**
 * What one fund resolves to on one axis, as published — weights within that
 * fund, not against a portfolio. Exported because Compare shows the same
 * breakdown per fund and must resolve it the same way this page does.
 *
 *
 * The two issuers publish different things and neither publishes both: Hamilton
 * states a fund-level sector breakdown, Harvest tags each holding with its own
 * sector and country. A fund-level allocation is used where there is one, and
 * otherwise the per-holding tags are rolled up into the same shape.
 *
 * Where the issuer says nothing either way, the app's own classification fills
 * the gap (src/data/sectors.ts) — that file is explicit about being ours rather
 * than published. Geography has no such fallback yet, so a fund publishing no
 * regions still resolves to nothing and is reported as unresolved.
 */
/** Enough of a fund's weight must resolve for the rollup to count as an answer.
    Below this it is a partial picture wearing the same clothes as a whole one:
    the shares would all be understated and nothing on screen would say so. */
const COVERAGE = 90;

export const bucketsFor = (symbol: string, axis: Axis, seen: ReadonlySet<string> = new Set()): Bucket[] => {
    if (axis === 'company') {
        return holdingsOf(symbol).map((h) => ({
            key: h.symbol ?? `name:${normalize(h.name)}`,
            name: h.name,
            ticker: h.symbol,
            weight: h.weight,
        }));
    }

    const kind = axis === 'sector' ? SECTOR_KIND : GEOGRAPHIC_KIND;
    const published = allocationsOf(symbol).filter((a) => a.kind === kind);
    if (published.length > 0) return published.map((a) => ({ key: `name:${normalize(a.name)}`, name: a.name, ticker: null, weight: a.weight }));

    // Three sources, in order of authority: a fund-level breakdown the issuer
    // published (above), a sector the issuer tagged on the holding itself, and
    // failing both the app's own classification. The last is what makes an
    // energy fund that publishes nothing still resolve to Energy.
    const rolled = new Map<string, Bucket>();
    const add = (label: string, weight: number): void => {
        const key = `name:${normalize(label)}`;
        const entry = rolled.get(key) ?? { key, name: label, ticker: null, weight: 0 };
        entry.weight += weight;
        rolled.set(key, entry);
    };

    // Only positive weights count toward coverage. The cash and written-option
    // lines are negative and belong to no bucket, so counting them would make a
    // fully classified fund look short.
    let claimable = 0;
    let resolvedWeight = 0;

    for (const h of holdingsOf(symbol)) {
        if (h.weight > 0) claimable += h.weight;

        const label = axis === 'sector' ? (h.sector ?? classifiedSector(symbol, h.symbol)) : (h.country ?? classifiedCountry(symbol, h.symbol));
        if (label != null) {
            add(label, h.weight);
            if (h.weight > 0) resolvedWeight += h.weight;
            continue;
        }

        // A holding that is itself a fund: look through it and weight its own
        // breakdown by this holding's weight. `seen` stops a fund that somehow
        // holds itself, directly or round a loop, from recursing forever.
        if (h.symbol == null || seen.has(h.symbol)) continue;
        const inner = bucketsFor(h.symbol, axis, new Set([...seen, symbol]));
        if (inner.length === 0) continue;

        const innerTotal = inner.reduce((sum, b) => sum + b.weight, 0);
        if (innerTotal <= 0) continue;
        for (const b of inner) add(b.name, (h.weight * b.weight) / innerTotal);
        if (h.weight > 0) resolvedWeight += h.weight;
    }

    if (claimable <= 0 || (resolvedWeight / claimable) * 100 < COVERAGE) return [];
    return [...rolled.values()];
};

/** Funds you hold that publish enough to resolve on this axis. */
export const coveredFunds = (axis: Axis): string[] => heldFunds().filter((s) => bucketsFor(s, axis).length > 0);

/** Funds you hold that do not — named rather than silently dropped, because a
    look-through missing a third of the book still looks like a whole answer. */
export const uncoveredFunds = (axis: Axis): string[] => heldFunds().filter((s) => bucketsFor(s, axis).length === 0);

/** The money actually resolved on this axis: the denominator every share uses. */
export const resolvedValue = (axis: Axis): number => coveredFunds(axis).reduce((sum, s) => sum + (valueByFund().get(s) ?? 0), 0);

/** The same, within one set of positions rather than the whole book. */
const resolvedIn = (byFund: ReadonlyMap<string, number>, axis: Axis): number =>
    [...byFund].reduce((sum, [symbol, value]) => (bucketsFor(symbol, axis).length > 0 ? sum + value : sum), 0);

/**
 * The rollup, over whatever set of positions it is handed.
 *
 * Parameterised rather than reading valueByFund directly, so the same
 * calculation answers "what is this book exposed to" and "what is this *account*
 * exposed to" — see accountExposures. Two implementations of a look-through is
 * two answers to a question that has one.
 */
const build = (byFund: ReadonlyMap<string, number>, axis: Axis): Exposure[] => {
    const total = resolvedIn(byFund, axis);
    const byKey = new Map<string, Exposure>();

    for (const [symbol, value] of byFund) {
        for (const b of bucketsFor(symbol, axis)) {
            const entry = byKey.get(b.key) ?? { key: b.key, name: b.name, ticker: b.ticker, slices: [], value: 0, share: null };
            const slice = (value * b.weight) / 100;

            // A fund listing the same bucket twice (share classes, or a holding
            // split across currencies) is one slice from that fund, not two —
            // otherwise it would count as its own overlap.
            const existing = entry.slices.find((s) => s.fund === symbol);
            if (existing) {
                existing.weight += b.weight;
                existing.value += slice;
            } else {
                entry.slices.push({ fund: symbol, weight: b.weight, value: slice });
            }

            entry.value += slice;
            byKey.set(b.key, entry);
        }
    }

    for (const entry of byKey.values()) {
        entry.slices.sort((a, b) => b.value - a.value);
        entry.share = total <= 0 ? null : (entry.value / total) * 100;
    }

    return [...byKey.values()].sort((a, b) => b.value - a.value);
};

const companyExposures = createMemo(() => build(valueByFund(), 'company'));
const sectorExposures = createMemo(() => build(valueByFund(), 'sector'));
const geographyExposures = createMemo(() => build(valueByFund(), 'geography'));

export const exposures = (axis: Axis): Exposure[] => (axis === 'company' ? companyExposures() : axis === 'sector' ? sectorExposures() : geographyExposures());

/** Market value per fund inside one account. */
const valueIn = (accountId: string): Map<string, number> => {
    const byFund = new Map<string, number>();
    for (const r of resolved()) {
        if (r.accountId !== accountId || r.value == null) continue;
        byFund.set(r.symbol, (byFund.get(r.symbol) ?? 0) + r.value);
    }
    return byFund;
};

/**
 * What one account is exposed to, on one axis.
 *
 * Shares are against what resolved *within this account*, not against the book:
 * the question on an account screen is what that account is made of, and a TFSA
 * showing 3% Financials because it is 3% of everything you own would be
 * answering a question nobody asked on that page.
 *
 * Not memoised, unlike the book-wide three. There are four accounts and three
 * axes, only one account is ever on screen, and a memo per pair would cache
 * eleven answers nobody is looking at.
 */
export const accountExposures = (accountId: string, axis: Axis): Exposure[] => build(valueIn(accountId), axis);

/** Market value per fund attributable to one loan — the borrowed share of each
    position it bought, at market. */
const valueOn = (loanId: string): Map<string, number> => {
    const byFund = new Map<string, number>();
    for (const r of resolved()) {
        if (r.value == null || r.holding.bookCost <= 0) continue;
        const borrowed = positionAt(r.accountId, r.symbol)?.loanCost.get(loanId) ?? 0;
        if (borrowed <= 0) continue;
        byFund.set(r.symbol, (byFund.get(r.symbol) ?? 0) + r.value * (borrowed / r.holding.bookCost));
    }
    return byFund;
};

/**
 * What one loan is exposed to, on one axis.
 *
 * The question the Loans page cannot otherwise answer: borrow against the house
 * to buy two covered-call funds and you may have bought the same five banks
 * twice with money you owe. Scoped to the borrowed share of each position, so
 * the shares are of the borrowed money rather than of the book.
 */
export const loanExposures = (loanId: string, axis: Axis): Exposure[] => build(valueOn(loanId), axis);

/**
 * The promise the site makes: every company you own more than once.
 *
 * A ticker is required, and that is the whole rule. Every fund here also
 * publishes aggregate lines — "Cash and Other Assets/Liabilities", "Written
 * Call Options" — with no ticker and, being the same words in every prospectus,
 * they match across all five funds and would top a list of things you own five
 * times. They are not companies. A ticker is the only identifier that says two
 * rows are the same company rather than the same phrase, so overlap is asserted
 * on it alone. Everything else is still visible under Company.
 */
export const overlapping = createMemo<Exposure[]>(() => companyExposures().filter((e) => e.ticker != null && e.slices.length > 1));

/** Companies proper: what the overlap count is a fraction of. */
export const identified = createMemo<Exposure[]>(() => companyExposures().filter((e) => e.ticker != null));

/** Dollars sitting in companies reached through more than one fund. */
export const overlappingValue = createMemo(() => overlapping().reduce((sum, e) => sum + e.value, 0));

/**
 * Everything the overlap list does not cover: reached through one fund, or with
 * no ticker to match on.
 *
 * The complement, computed from the same list rather than by re-filtering on the
 * rule — two filters that are meant to partition a set are two places for the
 * rule to change, and a company falling into both lists or neither is the kind
 * of error that reads as a data problem rather than a bug.
 */
export const notOverlapping = createMemo<Exposure[]>(() => {
    const twice = new Set(overlapping().map((e) => e.key));
    return companyExposures().filter((e) => !twice.has(e.key));
});
